import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronDown, Eye, Pencil, Plus, Power, RotateCcw, Search, Settings, X } from 'lucide-react';
import Modal from '../../../components/Modal';
import AgreementMailSettingsModal from '../../../components/workspace/contracts/AgreementMailSettingsModal';
import { useApi } from '../../../lib/api';
import { useWorkspace } from '../../../lib/workspace';

const trunc = (s, n = 25) => s && s.length > n ? s.slice(0, n) + '…' : (s || '');

const EMPTY_FORM = {
  client_id: '', rm_user_id: '', service_owner_id: '',
  start_date: '', end_date: '', billing_cycle: 'monthly',
  total_value: '', currency: 'INR', status: 'active', notes: '',
  agreement_name: '', mail_extra_cc: [],
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_EXTRA_CC = 4;
const MAX_EMAIL_LENGTH = 254;

const BILLING_CYCLES = ['monthly', 'quarterly', 'half_yearly', 'yearly', 'one_time'];

const SLA_TYPES = [
  { label: 'Fixed Day of Month', value: 'fixed_day' },
  { label: 'Relative Days',      value: 'relative_days' },
  { label: 'Specific Date',      value: 'specific_date' },
];

function StatusBadge({ status }) {
  const cls = status === 'active' ? 'status-done' : status === 'draft' ? 'status-pending' : 'status-cancelled';
  const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : '';
  return <span className={`status-pill ${cls}`}>{label}</span>;
}

function ViewField({ label, value, full }) {
  return (
    <div style={full ? { gridColumn: '1 / -1', paddingTop: 12, borderTop: '1px solid var(--border)' } : { paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 5 }}>{label}</div>
      <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', wordBreak: 'break-word', overflowWrap: 'anywhere', display: 'block' }}>{value || <span style={{ color: 'var(--text-muted)' }}>—</span>}</span>
    </div>
  );
}

const CLAUSE_MAX = 500;

function ClausesEditor({ clauses, onChange }) {
  const [draft, setDraft] = useState('');
  function add() {
    const v = draft.trim();
    if (!v) return;
    onChange([...clauses, v]);
    setDraft('');
  }
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={2} placeholder="Type a clause…"
            className="form-input" style={{ resize: 'none' }} maxLength={CLAUSE_MAX} />
        </div>
        <button onClick={add} className="btn-secondary" style={{ alignSelf: 'flex-end' }}>Add</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {clauses.map((c, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: 'var(--brand-light)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand)', flexShrink: 0, marginTop: 1 }}>{i + 1}.</span>
            <span style={{ flex: 1, fontSize: 13, color: 'var(--text-primary)', wordBreak: 'break-word', overflowWrap: 'anywhere', minWidth: 0 }}>{c}</span>
            <button onClick={() => onChange(clauses.filter((_, j) => j !== i))}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, flexShrink: 0 }}>
              <X size={13} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CustomDropdown({ value, onChange, options, placeholder = '— select —', disabled = false, zIndex = 200 }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    function onOutside(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);
  const filtered = options.filter(o => !search || o.label.toLowerCase().includes(search.toLowerCase()));
  const selected = options.find(o => String(o.value) === String(value));
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" disabled={disabled}
        onClick={() => { if (!disabled) { setOpen(o => !o); setSearch(''); } }}
        className="form-input"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, cursor: disabled ? 'not-allowed' : 'pointer', textAlign: 'left', width: '100%', opacity: disabled ? 0.5 : 1 }}>
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selected ? 'var(--text-primary)' : 'var(--text-muted)' }}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={14} style={{ flexShrink: 0, color: 'var(--text-muted)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,.15)', marginTop: 2, display: 'flex', flexDirection: 'column', maxHeight: 260 }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg-muted)', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 10px' }}>
              <Search size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search…" style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, width: '100%', color: 'var(--text-primary)' }} />
            </div>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0
              ? <div style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-muted)' }}>No results</div>
              : filtered.map(o => (
                <button key={o.value} type="button"
                  onClick={() => { onChange(String(o.value)); setOpen(false); setSearch(''); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', fontSize: 13, background: String(o.value) === String(value) ? 'var(--bg-muted)' : 'transparent', color: 'var(--text-primary)', border: 'none', cursor: 'pointer', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {o.label}
                </button>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

function ServiceLineCard({ svc, idx, services, onUpdate, onRemove }) {
  return (
    <div style={{ border: '1.5px solid var(--border-strong)', borderRadius: 10, padding: 16, background: 'var(--bg-muted)', minWidth: 0, overflow: 'hidden' }}>
      {/* Card header: title + Remove button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1px' }}>
          Service {idx + 1}
        </span>
        <button onClick={() => onRemove(idx)} style={{
          background: 'none', border: '1px solid var(--status-danger-fg)', color: 'var(--status-danger-fg)',
          borderRadius: 6, padding: '3px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600,
        }}>Remove</button>
      </div>

      {/* Row 1: Service (full width), Billing Cycle, Periodicity */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px', marginBottom: 10, minWidth: 0 }}>
        <label className="form-label" style={{ margin: 0, minWidth: 0 }}>
          <span>Service <span style={{ color: 'var(--status-danger-fg)' }}>*</span></span>
          <CustomDropdown
            value={String(svc.service_id || '')}
            onChange={v => onUpdate(idx, 'service_id', v)}
            options={services.map(s => ({ value: String(s.id), label: s.service_name }))}
            placeholder="— select service —"
            zIndex={300}
          />
        </label>
        <label className="form-label" style={{ margin: 0 }}>
          <span>Billing Cycle</span>
          <select className="form-input" value={svc.billing_cycle} onChange={e => onUpdate(idx, 'billing_cycle', e.target.value)}>
            {BILLING_CYCLES.map(b => <option key={b} value={b}>{b.replace(/_/g, ' ')}</option>)}
          </select>
        </label>
        <label className="form-label" style={{ margin: 0 }}>
          <span>Periodicity</span>
          <select className="form-input" value={svc.periodicity || 'monthly'} onChange={e => onUpdate(idx, 'periodicity', e.target.value)}>
            {['one_time','monthly','weekly','quarterly','half_yearly','yearly'].map(p =>
              <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>
            )}
          </select>
        </label>
      </div>

      {/* Row 2: Pricing — 4 columns */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px', marginBottom: 10, minWidth: 0 }}>
        <label className="form-label" style={{ margin: 0 }}>
          <span>Included Employees</span>
          <input type="number" className="form-input" min={0} value={svc.included_employee_count}
            onKeyDown={e => { if (e.key === '-' || e.key === 'e' || e.key === '+') e.preventDefault(); }}
            onChange={e => { const v = e.target.value; if (v === '' || (Number(v) >= 0 && String(Math.floor(Math.abs(Number(v)))).length <= 4)) onUpdate(idx, 'included_employee_count', v); }} />
        </label>
        <label className="form-label" style={{ margin: 0 }}>
          <span>Base Charge (₹)</span>
          <input type="number" className="form-input" min={0} value={svc.base_charge}
            onKeyDown={e => { if (e.key === '-' || e.key === 'e' || e.key === '+') e.preventDefault(); }}
            onChange={e => { const v = e.target.value; if (v === '' || (Number(v) >= 0 && Number(v) <= 10000000)) onUpdate(idx, 'base_charge', v); }} />
        </label>
        <label className="form-label" style={{ margin: 0 }}>
          <span>Add. Per Employee (₹)</span>
          <input type="number" className="form-input" min={0} value={svc.additional_charge_per_employee}
            onKeyDown={e => { if (e.key === '-' || e.key === 'e' || e.key === '+') e.preventDefault(); }}
            onChange={e => { const v = e.target.value; if (v === '' || (Number(v) >= 0 && Number(v) <= 10000000)) onUpdate(idx, 'additional_charge_per_employee', v); }} />
        </label>
        <label className="form-label" style={{ margin: 0 }}>
          <span>Effective From</span>
          <input type="date" className="form-input" value={svc.effective_from} onChange={e => onUpdate(idx, 'effective_from', e.target.value)} />
        </label>
      </div>

      {/* Row 3: SLA */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px', minWidth: 0 }}>
        <label className="form-label" style={{ margin: 0 }}>
          <span>SLA Type</span>
          <select className="form-input" value={svc.sla_type} onChange={e => onUpdate(idx, 'sla_type', e.target.value)}>
            {SLA_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>
        {svc.sla_type === 'fixed_day' && (
          <label className="form-label" style={{ margin: 0 }}>
            <span>Due Day (1–31)</span>
            <input type="number" min={1} max={31} className="form-input" value={svc.sla_due_day} onChange={e => onUpdate(idx, 'sla_due_day', e.target.value)} />
          </label>
        )}
        {svc.sla_type === 'relative_days' && (
          <label className="form-label" style={{ margin: 0 }}>
            <span>Relative Days</span>
            <input type="number" min={-365} max={365} className="form-input" value={svc.sla_relative_days} onChange={e => onUpdate(idx, 'sla_relative_days', e.target.value)} />
          </label>
        )}
        {svc.sla_type === 'specific_date' && (
          <>
            <label className="form-label" style={{ margin: 0 }}>
              <span>Month (1–12)</span>
              <input type="number" min={1} max={12} className="form-input" value={svc.sla_month} onChange={e => onUpdate(idx, 'sla_month', e.target.value)} />
            </label>
            <label className="form-label" style={{ margin: 0 }}>
              <span>Day (1–31)</span>
              <input type="number" min={1} max={31} className="form-input" value={svc.sla_date} onChange={e => onUpdate(idx, 'sla_date', e.target.value)} />
            </label>
          </>
        )}
      </div>
    </div>
  );
}

function emptyServiceLine() {
  return {
    service_id: '', billing_cycle: 'monthly', periodicity: 'monthly',
    included_employee_count: 100, base_charge: '', additional_charge_per_employee: 0,
    effective_from: new Date().toISOString().slice(0, 10),
    sla_type: 'fixed_day', sla_due_day: 5, sla_relative_days: 15, sla_month: 3, sla_date: 31,
  };
}

function svcLineFromExisting(s) {
  return {
    _cas_id:                        s.id,
    service_id:                     s.service_id || '',
    billing_cycle:                  s.billing_cycle || 'monthly',
    periodicity:                    s.periodicity || 'monthly',
    included_employee_count:        s.included_employee_count ?? 100,
    base_charge:                    s.base_charge ?? '',
    additional_charge_per_employee: s.additional_charge_per_employee ?? 0,
    effective_from:                 s.effective_from ? s.effective_from.slice(0, 10) : new Date().toISOString().slice(0, 10),
    sla_type:                       s.sla_type || 'fixed_day',
    sla_due_day:                    s.due_day ?? 5,
    sla_relative_days:              s.relative_days ?? 15,
    sla_month:                      s.sla_month ?? 3,
    sla_date:                       s.sla_date ?? 31,
  };
}

/* ── Agreement form ───────────────────────────────── */
function AgreementForm({ form, setForm, serviceLines, setServiceLines, clauses, setClauses,
  contractServices, appMembers, allocatedUserIds, setAllocatedUserIds, isEdit }) {

  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');

  function handleStartDateChange(val) {
    setForm(f => ({ ...f, start_date: val }));
    if (val && form.end_date && val > form.end_date) {
      setStartDateError('Start date cannot be greater than end date');
    } else {
      setStartDateError('');
      setEndDateError('');
    }
  }

  function handleEndDateChange(val) {
    setForm(f => ({ ...f, end_date: val }));
    if (val && form.start_date && val < form.start_date) {
      setEndDateError('End date cannot be less than start date');
    } else {
      setEndDateError('');
      setStartDateError('');
    }
  }

  const [userSearch, setUserSearch] = useState('');
  const [userDropOpen, setUserDropOpen] = useState(false);
  const userDropRef = useRef(null);

  function toggleUser(uid) {
    setAllocatedUserIds(prev =>
      prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]
    );
  }

  const filteredMembers = appMembers.filter(m =>
    !userSearch || m.name.toLowerCase().includes(userSearch.toLowerCase())
  );
  const selectedMembers = appMembers.filter(m => allocatedUserIds.includes(m.id));

  return (
    <div className="org-edit-form">
      <div className="org-form-section">
        <div className="org-form-section-title">Agreement Details</div>
        <div className="org-form-grid">
          <label className="form-label">
            <span>Agreement Name</span>
            <input className="form-input" placeholder="e.g. Annual Support Contract" maxLength={200}
              value={form.agreement_name || ''} onChange={e => setForm(f => ({ ...f, agreement_name: e.target.value }))} />
          </label>
          <label className="form-label">
            <span>Agreement Number</span>
            <input className="form-input" placeholder="Auto-generated if blank" maxLength={50}
              value={form.agreement_number || ''} onChange={e => setForm(f => ({ ...f, agreement_number: e.target.value }))} />
          </label>
          <label className="form-label">
            <span>Client <span style={{ color: 'var(--status-danger-fg)' }}>*</span></span>
            <CustomDropdown
              value={form.client_id}
              onChange={v => setForm(f => ({ ...f, client_id: v }))}
              options={(form._clients || []).map(c => ({
                value: String(c.id),
                label: c.client_name || c.client_code || String(c.id),
              }))}
              placeholder="— select client —"
              zIndex={1400}
            />
          </label>
          <label className="form-label">
            <span>Start Date <span style={{ color: 'var(--status-danger-fg)' }}>*</span></span>
            <input type="date" className="form-input" value={form.start_date} onChange={e => handleStartDateChange(e.target.value)} />
            {startDateError && (
              <span style={{ color: 'var(--status-danger-fg, #e53e3e)', fontSize: 12, marginTop: 3, display: 'block' }}>{startDateError}</span>
            )}
          </label>
          <label className="form-label">
            <span>End Date <span style={{ color: 'var(--status-danger-fg)' }}>*</span></span>
            <input type="date" className="form-input" value={form.end_date} onChange={e => handleEndDateChange(e.target.value)} />
            {endDateError && (
              <span style={{ color: 'var(--status-danger-fg, #e53e3e)', fontSize: 12, marginTop: 3, display: 'block' }}>{endDateError}</span>
            )}
          </label>
          <label className="form-label">
            <span>Relationship Manager</span>
            <CustomDropdown
              value={form.rm_user_id}
              onChange={v => setForm(f => ({ ...f, rm_user_id: v }))}
              options={appMembers.map(m => ({ value: String(m.id), label: m.name }))}
              placeholder="— select RM —"
              zIndex={1300}
            />
          </label>
          <label className="form-label">
            <span>Service Owner</span>
            <CustomDropdown
              value={form.service_owner_id}
              onChange={v => setForm(f => ({ ...f, service_owner_id: v }))}
              options={appMembers.map(m => ({ value: String(m.id), label: m.name }))}
              placeholder="— select service owner —"
              zIndex={1200}
            />
          </label>
          <label className="form-label">
            <span>Status</span>
            <select className="form-input" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="draft">Draft</option>
              <option value="expired">Expired</option>
              <option value="terminated">Terminated</option>
            </select>
          </label>
          <label className="form-label">
            <span>Billing Cycle</span>
            <select className="form-input" value={form.billing_cycle} onChange={e => setForm(f => ({ ...f, billing_cycle: e.target.value }))}>
              {BILLING_CYCLES.map(b => <option key={b} value={b}>{b.replace(/_/g, ' ')}</option>)}
            </select>
          </label>
          <label className="form-label">
            <span>Total Value (₹)</span>
            <input type="number" className="form-input" min={0} max={100000000} value={form.total_value}
              onChange={e => { const v = e.target.value; if (v === '' || Number(v) <= 100000000) setForm(f => ({ ...f, total_value: v })); }} />
          </label>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="form-label">
              <span>Notes</span>
              <textarea className="form-input" rows={2} style={{ resize: 'vertical' }} value={form.notes} maxLength={2000}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </label>
          </div>
        </div>
      </div>

      <div className="org-form-section">
        <div className="org-form-section-title">Important Clauses</div>
        <ClausesEditor clauses={clauses} onChange={setClauses} />
      </div>

      <div className="org-form-section">
        <div className="org-form-section-title">Visibility</div>
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-muted)' }}>
          <div style={{ padding: '6px 10px', borderBottom: '1px solid var(--border)' }}>
            <input type="text" placeholder="Search members…" value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              style={{ width: '100%', border: 'none', background: 'transparent', fontSize: 13, color: 'var(--text-primary)', outline: 'none' }} />
          </div>
          <div style={{ maxHeight: 180, overflowY: 'auto' }}>
            {filteredMembers.length === 0 ? (
              <div style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: 13 }}>No members found</div>
            ) : filteredMembers.map(m => {
              const selected = allocatedUserIds.includes(m.id);
              return (
                <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)', background: selected ? 'var(--bg-card)' : 'transparent' }}>
                  <input type="checkbox" checked={selected} onChange={() => toggleUser(m.id)}
                    style={{ accentColor: 'var(--accent)', width: 15, height: 15, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: 'var(--text-primary)', flex: 1 }}>{m.name}</span>
                </label>
              );
            })}
          </div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>Leave empty to make visible to all members.</div>
      </div>

      <div className="org-form-section">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div className="org-form-section-title" style={{ margin: 0, border: 'none', padding: 0 }}>Services</div>
          <button onClick={() => setServiceLines(prev => [...prev, emptyServiceLine()])}
            className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '6px 12px' }}>
            <Plus size={12} /> Add Service
          </button>
        </div>
        {serviceLines.length === 0 ? (
          <p className="muted" style={{ fontSize: 13, textAlign: 'center', padding: '16px 0' }}>No services added yet. Click "Add Service" to begin.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {serviceLines.map((svc, idx) => (
              <ServiceLineCard key={idx} svc={svc} idx={idx} services={contractServices}
                onUpdate={(i, f, v) => setServiceLines(prev => { const s = [...prev]; s[i] = { ...s[i], [f]: v }; return s; })}
                onRemove={i => setServiceLines(prev => prev.filter((_, j) => j !== i))} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Mail Settings popup for New Agreement (gear icon, matches Edit's own
   Mail Control icon position) — operates on the create form's local state
   directly instead of calling GET/PATCH .../mail-settings, since there's no
   agreement id yet for that endpoint to target. */
function CreateAgreementMailSettingsPopup({ open, onClose, form, setForm }) {
  const [newCcEmail, setNewCcEmail] = useState('');
  const [ccError, setCcError]       = useState('');
  const extraCc = form.mail_extra_cc || [];

  useEffect(() => {
    if (open) { setNewCcEmail(''); setCcError(''); }
  }, [open]);

  function extraCcError(email) {
    if (email.length > MAX_EMAIL_LENGTH) return `That email is too long (max ${MAX_EMAIL_LENGTH} characters)`;
    if (!EMAIL_RE.test(email)) return 'Enter a valid email address, e.g. name@company.com';
    if (extraCc.length >= MAX_EXTRA_CC) return `You can add at most ${MAX_EXTRA_CC} extra CC recipients`;
    if (extraCc.includes(email)) return `"${email}" already exists in the CC list`;
    return '';
  }

  function addExtraCc() {
    const email = newCcEmail.trim().toLowerCase();
    if (!email) return;
    const err = extraCcError(email);
    setCcError(err);
    if (err) return;
    setForm(f => ({ ...f, mail_extra_cc: [...(f.mail_extra_cc || []), email] }));
    setNewCcEmail('');
  }

  function removeExtraCc(email) {
    setForm(f => ({ ...f, mail_extra_cc: (f.mail_extra_cc || []).filter(e => e !== email) }));
  }

  return (
    <Modal open={open} onClose={onClose} title="Mail Control" size="sm">
      <p style={{ fontSize: 12, color: 'var(--fg-dim)', marginTop: 0, marginBottom: 16, lineHeight: 1.5 }}>
        When every service on this agreement is completed and approved, GLOS emails the client
        with the agreement details and the completed service list.
      </p>
      <div>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg)' }}>Notify</span>
        <p style={{ fontSize: 12, color: 'var(--fg-dim)', margin: '4px 0 12px', lineHeight: 1.5 }}>
          Up to {MAX_EXTRA_CC} additional email addresses to CC on that mail, alongside the client,
          relationship manager and app owner.
        </p>
        {extraCc.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            {extraCc.map(email => (
              <div key={email} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '5px 10px', background: 'var(--bg-3, rgba(0,0,0,0.03))',
                border: '1px solid var(--border)', borderRadius: 7, fontSize: 12.5,
              }}>
                <span style={{ color: 'var(--fg)' }}>{email}</span>
                <button
                  onClick={() => removeExtraCc(email)}
                  title="Remove"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--fg-mute, #94a3b8)', fontSize: 13, padding: '0 2px', lineHeight: 1 }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
        {extraCc.length < MAX_EXTRA_CC && (
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="email"
              className="form-input"
              placeholder="name@company.com"
              value={newCcEmail}
              onChange={e => { setNewCcEmail(e.target.value); setCcError(''); }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addExtraCc(); } }}
              maxLength={MAX_EMAIL_LENGTH}
              style={{ margin: 0, flex: 1, fontSize: 12.5 }}
            />
            <button
              className="btn-secondary"
              onClick={addExtraCc}
              disabled={!newCcEmail.trim()}
              style={{ padding: '4px 14px', fontSize: 12.5, flexShrink: 0 }}
            >
              + Add
            </button>
          </div>
        )}
        {ccError && <div className="form-error" style={{ marginTop: 10 }}>{ccError}</div>}
      </div>
    </Modal>
  );
}

export default function Agreements() {
  const { appId } = useParams();
  const { myRole, me } = useWorkspace();
  const api = useApi();

  const isAdmin = true; // all members can add agreements
  const isRoleAdmin = myRole === 'super_admin' || myRole === 'admin';
  const myUserId = me?.id;
  // Editing (unlike creating) is admin-, creator-, RM-, or Service
  // Owner-only server-side (see canEditAgreement in
  // backend/contracts/agreements.js) — a plain allocated/task-assignee
  // viewer would get a 403 if they tried, so the Edit icon is only shown to
  // whoever can actually use it.
  const canEditAgreement = a => isRoleAdmin
    || String(a.created_by_user_id) === String(myUserId)
    || String(a.rm_user_id) === String(myUserId)
    || String(a.service_owner_id) === String(myUserId);

  const [agreements, setAgreements]           = useState([]);
  const [clients, setClients]                 = useState([]);
  const [contractServices, setContractServices] = useState([]);
  const [appMembers, setAppMembers]           = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState(null);
  const [search, setSearch]                   = useState('');
  const [statusFilter, setStatus]             = useState('');
  const [clientFilter, setClientFilter]       = useState('');

  const [createOpen, setCreateOpen]           = useState(false);
  const [form, setForm]                       = useState({ ...EMPTY_FORM });
  const [serviceLines, setServiceLines]       = useState([]);
  const [clauses, setClauses]                 = useState([]);
  const [allocatedUserIds, setAllocatedUserIds] = useState([]);
  const [saving, setSaving]                   = useState(false);
  const [saveErr, setSaveErr]                 = useState(null);

  const [editOpen, setEditOpen]               = useState(false);
  const [editing, setEditing]                 = useState(null);
  const [editForm, setEditForm]               = useState({ ...EMPTY_FORM });
  const [editServiceLines, setEditServiceLines] = useState([]);
  const [editClauses, setEditClauses]         = useState([]);
  const [editAllocatedUserIds, setEditAllocatedUserIds] = useState([]);
  const [editSaving, setEditSaving]           = useState(false);
  const [editErr, setEditErr]                 = useState(null);

  const [viewModal, setViewModal]             = useState(null);
  const [statusTarget, setStatusTarget]       = useState(null); // agreement being (in)activated
  const [statusSaving, setStatusSaving]       = useState(false);
  const [statusErr, setStatusErr]             = useState(null);
  const [mailSettingsOpen, setMailSettingsOpen] = useState(false);
  const [createMailSettingsOpen, setCreateMailSettingsOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (clientFilter) params.client_id = clientFilter;
      const [agData, clData, svcData, memberData] = await Promise.all([
        api.listContractAgreements(appId, params),
        api.listContractClients(appId, { status: 'active' }),
        api.listContractServices(appId, {}),
        api.listAppMembers(appId),
      ]);
      setAgreements(agData);
      setClients(clData);
      setContractServices(svcData);
      const members = (memberData?.items || memberData || []).map(m => ({
        id: m.user_id || m.id,
        name: [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email || m.name || '',
      }));
      setAppMembers(members);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [appId, search, statusFilter, clientFilter]);

  useEffect(() => { load(); }, [load]);

  function withClients(f) { return { ...f, _clients: clients }; }

  function openCreate() {
    setForm(withClients({ ...EMPTY_FORM }));
    setServiceLines([]);
    setClauses([]);
    setAllocatedUserIds([]);
    setSaveErr(null);
    setCreateOpen(true);
  }

  function openEdit(a) {
    setEditing(a);
    setEditForm(withClients({
      agreement_number: a.agreement_number || '',
      agreement_name:   a.agreement_name || '',
      client_id:        a.client_id ?? '',
      rm_user_id:       a.rm_user_id ?? '',
      service_owner_id: a.service_owner_id ?? '',
      start_date:       a.start_date ? a.start_date.slice(0, 10) : '',
      end_date:         a.end_date   ? a.end_date.slice(0, 10)   : '',
      billing_cycle:    a.billing_cycle || 'monthly',
      total_value:      a.total_value ?? '',
      currency:         a.currency || 'INR',
      status:           a.status || 'active',
      notes:            a.notes || '',
    }));
    setEditClauses(a.clauses || []);
    setEditServiceLines((a.services || []).map(svcLineFromExisting));
    setEditAllocatedUserIds((a.allocated_users || []).map(u => u.id));
    setEditErr(null);
    setEditOpen(true);
  }

  function buildBody(f, svcLines, cls, userIds) {
    return {
      ...f,
      _clients:         undefined,
      total_value:      f.total_value === '' ? null : Number(f.total_value),
      rm_user_id:       f.rm_user_id || null,
      service_owner_id: f.service_owner_id || null,
      clauses:          cls,
      allocated_user_ids: userIds,
      services: svcLines.map(s => ({
        ...(s._cas_id ? { cas_id: s._cas_id } : {}),
        service_id:    s.service_id,
        billing_cycle: s.billing_cycle,
        periodicity:   s.periodicity || 'monthly',
        pricing: {
          included_employee_count:        Number(s.included_employee_count),
          base_charge:                    Number(s.base_charge),
          additional_charge_per_employee: Number(s.additional_charge_per_employee),
          effective_from:                 s.effective_from,
        },
        sla: {
          sla_type:      s.sla_type,
          due_day:       s.sla_type === 'fixed_day'     ? Number(s.sla_due_day)       : null,
          relative_days: s.sla_type === 'relative_days' ? Number(s.sla_relative_days) : null,
          sla_month:     s.sla_type === 'specific_date' ? Number(s.sla_month)         : null,
          sla_date:      s.sla_type === 'specific_date' ? Number(s.sla_date)          : null,
        },
      })),
    };
  }

  async function handleCreate() {
    if (!form.client_id)  { setSaveErr('Client is required'); return; }
    if (!form.start_date) { setSaveErr('Start date is required'); return; }
    if (!form.end_date)   { setSaveErr('End date is required'); return; }
    if (form.end_date < form.start_date) { setSaveErr('End date cannot be less than start date'); return; }
    if (form.total_value !== '' && Number(form.total_value) > 100000000) { setSaveErr('Total value cannot exceed ₹10 crore (₹10,00,00,000)'); return; }
    setSaving(true); setSaveErr(null);
    try {
      await api.createContractAgreement(appId, buildBody(form, serviceLines, clauses, allocatedUserIds));
      setCreateOpen(false);
      load();
    } catch (e) {
      setSaveErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleEdit() {
    if (editForm.start_date && editForm.end_date && editForm.end_date < editForm.start_date) { setEditErr('End date cannot be less than start date'); return; }
    if (editForm.total_value !== '' && Number(editForm.total_value) > 100000000) { setEditErr('Total value cannot exceed ₹10 crore (₹10,00,00,000)'); return; }
    setEditSaving(true); setEditErr(null);
    try {
      await api.updateContractAgreement(appId, editing.id, buildBody(editForm, editServiceLines, editClauses, editAllocatedUserIds));
      setEditOpen(false);
      load();
    } catch (e) {
      setEditErr(e.message || 'Save failed');
    } finally {
      setEditSaving(false);
    }
  }

  async function handleToggleActive() {
    const a = statusTarget;
    setStatusSaving(true); setStatusErr(null);
    try {
      await api.updateContractAgreement(appId, a.id, { status: a.status === 'inactive' ? 'active' : 'inactive' });
      setStatusTarget(null);
      load();
    } catch (e) {
      setStatusErr(e.message || 'Update failed');
    } finally {
      setStatusSaving(false);
    }
  }

  const clientName = (a) => a.client?.client_name || clients.find(c => String(c.id) === String(a.client_id))?.client_name || '—';

  return (
    <div className="tl-page">
      <div className="tl-toolbar">
        <div className="tl-search-wrap">
          <Search className="tl-search-icon" size={14} />
          <input className="tl-search-input" placeholder="Search agreements…" value={search} maxLength={1000} onChange={e => setSearch(e.target.value)} />
          {search && <button className="tl-search-clear" onClick={() => setSearch('')}>×</button>}
        </div>
        <div style={{ minWidth: 180, maxWidth: 260 }}>
          <CustomDropdown
            value={clientFilter || '__all__'}
            onChange={v => setClientFilter(v === '__all__' ? '' : v)}
            options={[
              { value: '__all__', label: 'All Clients' },
              ...clients.map(c => ({ value: String(c.id), label: c.client_name || c.client_code || String(c.id) })),
            ]}
            placeholder="All Clients"
            zIndex={300}
          />
        </div>
        <select className="tl-select" value={statusFilter} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="expired">Expired</option>
          <option value="terminated">Terminated</option>
          <option value="draft">Draft</option>
        </select>
        {isAdmin && (
          <button className="btn-primary" onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> New Agreement
          </button>
        )}
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      {loading ? (
        <div className="tl-loading"><span className="tl-spinner" /> Loading…</div>
      ) : agreements.length === 0 ? (
        <div className="tl-empty">
          <div className="tl-empty-title">No agreements found</div>
          <p className="tl-empty-sub">Create your first agreement to get started.</p>
        </div>
      ) : (
        <div className="entity-table-wrap">
          <table className="entity-table">
            <thead>
              <tr>
                <th style={{ width: 60 }}>Sl No</th>
                <th style={{ width: 200 }}>Agreement</th>
                <th style={{ width: 90 }}>Code</th>
                <th style={{ width: 180 }}>Client</th>
                <th style={{ width: 220 }}>Period</th>
                <th style={{ width: 180 }}>Services</th>
                <th style={{ width: 120 }}>Billing</th>
                <th style={{ width: 100 }}>Status</th>
                <th style={{ width: 100 }} className="row-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {agreements.map((a, idx) => (
                <tr key={a.id}>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{idx + 1}</td>
                  <td>
                    {a.agreement_name ? <span style={{ fontWeight: 600, whiteSpace: 'normal', wordBreak: 'break-word' }}>{a.agreement_name}</span> : <span className="muted">—</span>}
                  </td>
                  <td>
                    <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 12, color: 'var(--brand)', background: 'var(--bg-muted)', padding: '2px 7px', borderRadius: 4, whiteSpace: 'normal', wordBreak: 'break-word' }}>{a.agreement_number || '—'}</span>
                  </td>
                  <td style={{ fontWeight: 600, whiteSpace: 'normal', wordBreak: 'break-word' }}>{clientName(a)}</td>
                  <td title={[a.start_date, a.end_date].filter(Boolean).map(d => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })).join(' – ')}>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{a.start_date ? new Date(a.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{a.end_date ? new Date(a.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</div>
                  </td>
                  <td title={a.services?.map(s => s.service_name || s.service_code).join(', ')}>
                    {a.services?.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 4, overflow: 'hidden' }}>
                        {a.services.slice(0, 2).map((s, j) => (
                          <span key={j} style={{ padding: '2px 8px', background: 'var(--brand-light)', color: 'var(--brand)', borderRadius: 20, fontSize: 11, fontWeight: 600, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            {s.service_name || s.service_code}
                          </span>
                        ))}
                        {a.services.length > 2 && (
                          <span className="muted" style={{ padding: '2px 8px', background: 'var(--bg-muted)', borderRadius: 20, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
                            +{a.services.length - 2}
                          </span>
                        )}
                      </div>
                    ) : <span className="muted">—</span>}
                  </td>
                  <td className="muted" title={a.billing_cycle?.replace(/_/g, ' ')}>{a.billing_cycle?.replace(/_/g, ' ') || '—'}</td>
                  <td><StatusBadge status={a.status} /></td>
                  <td className="row-actions">
                    <button className="btn-link-sm" onClick={() => setViewModal(a)} title="View details"><Eye size={13} /></button>
                    {canEditAgreement(a) && <button className="btn-link-sm" onClick={() => openEdit(a)} title="Edit"><Pencil size={13} /></button>}
                    {canEditAgreement(a) && (
                      a.status === 'inactive'
                        ? <button className="btn-link-sm" onClick={() => { setStatusErr(null); setStatusTarget(a); }} title="Reactivate"><RotateCcw size={13} /></button>
                        : <button className="btn-link-sm" onClick={() => { setStatusErr(null); setStatusTarget(a); }} title="Make inactive" style={{ color: 'var(--status-danger-fg)' }}><Power size={13} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Inactivate / Reactivate confirm ──────────────── */}
      <Modal open={!!statusTarget}
        title={statusTarget?.status === 'inactive' ? 'Reactivate Agreement' : 'Make Agreement Inactive'}
        onClose={() => !statusSaving && setStatusTarget(null)} size="sm"
        footer={
          <div className="form-actions">
            <button className="btn-secondary" onClick={() => setStatusTarget(null)} disabled={statusSaving}>Cancel</button>
            <button className="btn-primary" onClick={handleToggleActive} disabled={statusSaving}>
              {statusSaving ? 'Saving…' : statusTarget?.status === 'inactive' ? 'Reactivate' : 'Make Inactive'}
            </button>
          </div>
        }
      >
        {statusTarget && (
          <div style={{ fontSize: 13.5, lineHeight: 1.6 }}>
            {statusTarget.status === 'inactive' ? (
              <p style={{ margin: 0 }}>
                Reactivate <strong>{statusTarget.agreement_name || statusTarget.agreement_number}</strong>? It will show in
                the agreement list again and compliance tasks will resume.
              </p>
            ) : (
              <p style={{ margin: 0 }}>
                Make <strong>{statusTarget.agreement_name || statusTarget.agreement_number}</strong> inactive? It will be
                hidden from the agreement list and task pickers, and no new compliance tasks will be created. Existing
                tasks and history are kept. Use the <strong>Inactive</strong> status filter to see it again.
              </p>
            )}
            {statusErr && <div className="banner banner-error" style={{ marginTop: 12 }}>{statusErr}</div>}
          </div>
        )}
      </Modal>

      {/* ── Create Modal ─────────────────────────────────── */}
      <Modal open={createOpen} title="New Agreement" onClose={() => setCreateOpen(false)} size="xl"
        headerExtra={(
          <button
            onClick={() => setCreateMailSettingsOpen(true)}
            title="Mail Control"
            aria-label="Mail Control"
            style={{ background: 'none', border: 'none', padding: 4, lineHeight: 1, color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex' }}
          >
            <Settings size={18} />
          </button>
        )}
        footer={
          <div className="form-actions">
            <button className="btn-secondary" onClick={() => setCreateOpen(false)} disabled={saving}>Cancel</button>
            <button className="btn-primary" onClick={handleCreate} disabled={saving}>{saving ? 'Saving…' : 'Create Agreement'}</button>
          </div>
        }
      >
        <AgreementForm
          form={form} setForm={setForm}
          serviceLines={serviceLines} setServiceLines={setServiceLines}
          clauses={clauses} setClauses={setClauses}
          contractServices={contractServices}
          appMembers={appMembers}
          allocatedUserIds={allocatedUserIds} setAllocatedUserIds={setAllocatedUserIds}
        />
        {saveErr && <div className="banner banner-error" style={{ marginTop: 8 }}>{saveErr}</div>}
      </Modal>

      <CreateAgreementMailSettingsPopup
        open={createMailSettingsOpen}
        onClose={() => setCreateMailSettingsOpen(false)}
        form={form}
        setForm={setForm}
      />

      {/* ── Edit Modal ───────────────────────────────────── */}
      <Modal open={editOpen} title="Edit Agreement" onClose={() => setEditOpen(false)} size="xl"
        headerExtra={editing && canEditAgreement(editing) ? (
          <button
            onClick={() => setMailSettingsOpen(true)}
            title="Mail Control"
            aria-label="Mail Control"
            style={{ background: 'none', border: 'none', padding: 4, lineHeight: 1, color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex' }}
          >
            <Settings size={18} />
          </button>
        ) : null}
        footer={
          <div className="form-actions">
            <button className="btn-secondary" onClick={() => setEditOpen(false)} disabled={editSaving}>Cancel</button>
            <button className="btn-primary" onClick={handleEdit} disabled={editSaving}>{editSaving ? 'Saving…' : 'Save Changes'}</button>
          </div>
        }
      >
        <AgreementForm
          form={editForm} setForm={setEditForm}
          serviceLines={editServiceLines} setServiceLines={setEditServiceLines}
          clauses={editClauses} setClauses={setEditClauses}
          contractServices={contractServices}
          appMembers={appMembers}
          allocatedUserIds={editAllocatedUserIds} setAllocatedUserIds={setEditAllocatedUserIds}
          isEdit
        />
        {editErr && <div className="banner banner-error" style={{ marginTop: 8 }}>{editErr}</div>}
      </Modal>

      <AgreementMailSettingsModal
        open={mailSettingsOpen}
        onClose={() => setMailSettingsOpen(false)}
        appId={appId}
        agreement={editing}
        canEdit={!!(editing && canEditAgreement(editing))}
      />

      {/* ── View Modal ───────────────────────────────────── */}
      {viewModal && (
        <Modal open title="Agreement Details" onClose={() => setViewModal(null)} size="lg"
          footer={null}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 20px' }}>
              <ViewField label="Agreement #"   value={viewModal.agreement_number} />
              <ViewField label="Agreement Name" value={viewModal.agreement_name} />
              <ViewField label="Client"        value={clientName(viewModal)} />
              <ViewField label="Status"        value={<StatusBadge status={viewModal.status} />} />
              <ViewField label="Start Date"    value={viewModal.start_date ? viewModal.start_date.slice(0, 10) : null} />
              <ViewField label="End Date"      value={viewModal.end_date   ? viewModal.end_date.slice(0, 10)   : null} />
              <ViewField label="Billing"       value={viewModal.billing_cycle?.replace(/_/g, ' ')} />
              <ViewField label="Total Value"   value={viewModal.total_value != null ? `${viewModal.currency || 'INR'} ${Number(viewModal.total_value).toLocaleString()}` : null} />
              <ViewField label="Service Owner" value={viewModal.service_owner?.name} />
              <ViewField label="Rel. Manager"  value={viewModal.rm_user?.name} />
            </div>

            {viewModal.notes && (
              <div style={{ paddingTop: 12, borderTop: '1px solid var(--border)', marginTop: 4 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 5 }}>Notes</div>
                <div style={{ fontSize: 14, color: 'var(--text-primary)', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{viewModal.notes}</div>
              </div>
            )}

            {viewModal.clauses?.length > 0 && (
              <div style={{ paddingTop: 16, borderTop: '1px solid var(--border)', marginTop: 4 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 10 }}>Important Clauses</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {viewModal.clauses.map((c, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, background: 'var(--brand-light)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 13, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand)', flexShrink: 0 }}>{i + 1}.</span>{c}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {viewModal.services?.length > 0 && (
              <div style={{ paddingTop: 16, borderTop: '1px solid var(--border)', marginTop: 4 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 10 }}>Services</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {viewModal.services.map(s => (
                    <div key={s.id} style={{ background: 'var(--bg-muted)', borderRadius: 8, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{s.service_name || s.service_code}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                          {s.periodicity?.replace(/_/g, ' ')}
                        </div>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'right' }}>
                        {s.base_charge != null && <div>Base: ₹{Number(s.base_charge || 0).toLocaleString()}</div>}
                        {s.included_employee_count != null && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.included_employee_count} employees</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ paddingTop: 16, borderTop: '1px solid var(--border)', marginTop: 4 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 10 }}>Visible To</div>
              {viewModal.allocated_users?.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {viewModal.allocated_users.map(u => (
                    <span key={u.id} style={{ padding: '4px 12px', background: 'var(--brand-light)', color: 'var(--brand)', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{u.name}</span>
                  ))}
                </div>
              ) : (
                <span style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>All members</span>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
